import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { useToast } from "@/contexts/toaster";
import { parseError } from "@/lib/parse-error";
import type { BookableDetail, BookableUpsertPayload } from "@/types/bookable";
import type { ServiceTable } from "@/types/service";
import { deleteBookable, listBookables, updateOrCreateBookable } from "./bookable";
import { serviceQueryKeys } from "./service-query";
import { QueuedMutation, invalidateByKey, revertOnError, startOptimisticChange } from "@/api/utils/query-helpers";
export const bookableQueryKeys = {
  all: ["provider", "admin", "bookables"] as const,
  list: () => [...bookableQueryKeys.all, "list"] as const
};
export const bookableQueryOptions = {
  list: () => ({
    queryKey: bookableQueryKeys.list(),
    queryFn: () => listBookables(),
    select: (data: BookableDetail[] | null) => data ?? []
  })
};
const BOOKABLE_UPSERT_DEBOUNCE_MS = 350;
const latestOptimisticVersionById = new Map<string, number>();
let optimisticVersion = 0;
function getLatestBookablePayload(bookableId: string, fallback: BookableUpsertPayload): BookableUpsertPayload {
  const current = queryClient.getQueryData<BookableDetail[] | undefined>(bookableQueryKeys.list());
  const cached = current?.find(bookable => bookable.id === bookableId);
  if (!cached) return fallback;
  return {
    id: cached.id,
    name: cached.name,
    description: cached.description,
    locationCapacities: cached.locationCapacities,
    defaultCapacity: cached.defaultCapacity,
    defaultOutOfOrderCount: cached.defaultOutOfOrderCount,
    requiredStaffIds: cached.requiredStaffIds
  };
}
const bookableUpsertQueue = new QueuedMutation<string, BookableUpsertPayload, BookableDetail | null>({
  debounceMs: BOOKABLE_UPSERT_DEBOUNCE_MS,
  getKey: bookable => bookable.id,
  merge: (previous, next) => ({
    ...previous,
    ...next
  }),
  getPayload: getLatestBookablePayload,
  mutationFn: updateOrCreateBookable
});
function nextOptimisticVersion(bookableId: string): number {
  optimisticVersion += 1;
  latestOptimisticVersionById.set(bookableId, optimisticVersion);
  return optimisticVersion;
}
function isLatestOptimisticVersion(bookableId: string, version: number) {
  return latestOptimisticVersionById.get(bookableId) === version;
}
export const bookableMutations = {
  upsert: async (bookable: BookableUpsertPayload) => await bookableUpsertQueue.enqueue(bookable),
  delete: async (bookableId: string) => await deleteBookable(bookableId)
};
function sortBookables(items: BookableDetail[]): BookableDetail[] {
  return [...items].sort((a, b) => {
    const an = (a.name ?? "").toLowerCase();
    const bn = (b.name ?? "").toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return a.id.localeCompare(b.id);
  });
}
export const useUpsertBookableMutation = () => {
  const $ = _c(2);
  const {
    toast
  } = useToast();
  let t0;
  if ($[0] !== toast) {
    t0 = {
      mutationFn: _temp,
      onMutate: _temp4,
      onError: async (err, _vars, ctx) => {
        if (!ctx || !isLatestOptimisticVersion(ctx.bookableId, ctx.optimisticVersion)) {
          return;
        }
        revertOnError(ctx.key, ctx.previous);
        const description = await parseError(err, "Failed to save bookable.");
        toast({
          title: "Couldn't save bookable",
          description,
          variant: "error"
        });
      },
      onSuccess: _temp5,
      onSettled: _temp6
    };
    $[0] = toast;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useDeleteBookableMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp7,
      onMutate: _temp8,
      onError: _temp9,
      onSettled: _temp10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};

// Where the backend starts a new link: the service phase, after any prep.
const serviceStartOffset = (service: ServiceTable) => (service.prepTimeDuration ?? 0) + ((service.prepTimeDuration ?? 0) > 0 ? service.prepTimeBuffer ?? 0 : 0);
function withBookableLink(service: ServiceTable, bookableId: string, linked: boolean): ServiceTable {
  const bookables = service.bookables ?? [];
  if (bookables.some(b => b.bookableId === bookableId) === linked) {
    return service;
  }
  return {
    ...service,
    bookables: linked ? [...bookables, {
      bookableId,
      startOffset: serviceStartOffset(service),
      duration: null
    }] : bookables.filter(b => b.bookableId !== bookableId)
  };
}

/** Sets which services reserve a bookable, straight from the bookables table. */
export const useSetBookableServicesMutation = () => {
  const $ = _c(2);
  const {
    toast
  } = useToast();
  let t0;
  if ($[0] !== toast) {
    t0 = {
      mutationFn: _temp11,
      onMutate: _temp12,
      onError: async (err, _vars, ctx) => {
        for (const [key_0, data_0] of ctx?.previous ?? []) {
          queryClient.setQueryData(key_0, data_0);
        }
        const description = await parseError(err, "Failed to save services.");
        toast({
          title: "Couldn't update services",
          description,
          variant: "error"
        });
      }
    };
    $[0] = toast;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
async function _temp(t0) {
  const {
    data
  } = t0;
  return await bookableUpsertQueue.enqueue(data);
}
function _temp2(loc) {
  return loc.locationId;
}
function _temp3(loc_0) {
  return loc_0.locationId;
}
async function _temp4(t0) {
  const {
    data: data_0
  } = t0;
  const key = bookableQueryKeys.list();
  const tempId = data_0.id ?? `tmp-bkl-${Date.now()}`;
  const optimisticData = {
    ...data_0,
    id: tempId
  } as BookableUpsertPayload & {
    id: string;
  };
  const mutationVersion = nextOptimisticVersion(tempId);
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const current = queryClient.getQueryData(key);
    const index = current?.findIndex(b => b.id === optimisticData.id) ?? -1;
    if (current && index >= 0) {
      const updated = {
        ...current[index],
        ...optimisticData,
        description: optimisticData.description ?? current[index].description ?? "",
        locationIds: optimisticData.locationCapacities?.map(_temp2) ?? current[index].locationIds ?? [],
        locationCapacities: optimisticData.locationCapacities ?? current[index].locationCapacities ?? []
      } as BookableDetail;
      const next = sortBookables([...current.slice(0, index), updated, ...current.slice(index + 1)]);
      queryClient.setQueryData(key, next);
    } else {
      const optimistic = {
        id: optimisticData.id,
        name: optimisticData.name ?? optimisticData.id,
        description: optimisticData.description ?? "",
        locationIds: optimisticData.locationCapacities?.map(_temp3) ?? [],
        defaultCapacity: optimisticData.defaultCapacity ?? null,
        defaultOutOfOrderCount: optimisticData.defaultOutOfOrderCount ?? 0,
        locationCapacities: optimisticData.locationCapacities ?? [],
        requiredStaffIds: optimisticData.requiredStaffIds ?? []
      } as BookableDetail;
      const next_0 = sortBookables([...(current ?? []), optimistic]);
      queryClient.setQueryData(key, next_0);
    }
  });
  return {
    key,
    previous,
    tempId,
    bookableId: tempId,
    optimisticVersion: mutationVersion
  };
}
function _temp5(server, _vars_0, ctx_0) {
  if (!server || !ctx_0?.tempId) {
    return;
  }
  if (!isLatestOptimisticVersion(ctx_0.bookableId, ctx_0.optimisticVersion)) {
    return;
  }
  latestOptimisticVersionById.set(server.id, ctx_0.optimisticVersion);
  const key_0 = bookableQueryKeys.list();
  const current_0 = queryClient.getQueryData(key_0);
  if (!current_0) {
    return;
  }
  const idx = current_0.findIndex(b_0 => b_0.id === ctx_0.tempId);
  if (idx >= 0) {
    const next_1 = [...current_0];
    next_1[idx] = {
      ...next_1[idx],
      ...server
    } as BookableDetail;
    queryClient.setQueryData(key_0, sortBookables(next_1));
  }
}
function _temp6(_server, _error, _vars_1, ctx_1) {
  if (ctx_1 && isLatestOptimisticVersion(ctx_1.bookableId, ctx_1.optimisticVersion)) {
    return invalidateByKey(bookableQueryKeys.list());
  }
}
async function _temp7(t0) {
  const {
    id
  } = t0;
  return await deleteBookable(id);
}
async function _temp8(t0) {
  const {
    id: id_0
  } = t0;
  const key = bookableQueryKeys.list();
  const {
    previous
  } = await startOptimisticChange(key, () => {
    const current = queryClient.getQueryData(key);
    if (!current) {
      return;
    }
    queryClient.setQueryData(key, current.filter(b => b.id !== id_0));
  });
  return {
    key,
    previous
  };
}
function _temp9(_err, _vars, ctx) {
  if (ctx) {
    revertOnError(ctx.key, ctx.previous);
  }
}
function _temp10() {
  return invalidateByKey(bookableQueryKeys.list());
}
function _temp11(t0) {
  const {
    bookableId,
    serviceIds
  } = t0;
  return updateOrCreateBookable({
    id: bookableId,
    serviceIds
  });
}
async function _temp12(t0) {
  const {
    bookableId: bookableId_0,
    serviceIds: serviceIds_0
  } = t0;
  const wanted = new Set(serviceIds_0);
  await queryClient.cancelQueries({
    queryKey: serviceQueryKeys.all
  });
  const previous = queryClient.getQueriesData({
    queryKey: serviceQueryKeys.all
  });
  for (const [key, data] of previous) {
    if (Array.isArray(data)) {
      queryClient.setQueryData(key, (data as ServiceTable[]).map(service => withBookableLink(service, bookableId_0, wanted.has(service.id))));
    } else {
      if (data) {
        const service_0 = data as ServiceTable;
        queryClient.setQueryData(key, withBookableLink(service_0, bookableId_0, wanted.has(service_0.id)));
      }
    }
  }
  return {
    previous
  };
}